import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { selectAppConfig } from '../../redux/appConfigReducer';
import { selectFilters } from '../../redux/filtersReducer';
import { selectLocation } from '../../redux/locationReducer';
import { USED_COMBINED_FILTERS_LIST_TIER_3 } from '../../constants/initialFilters';

const useApiFiltersProcessor = () => {
  const { location } = useSelector(selectLocation);
  const { confirmedFilters } = useSelector(selectFilters);
  const { isTier3page, listOfDealers, marketType, dataSource } = useSelector(selectAppConfig);

  const {
    availableInMonths,
    bodyType,
    drivetrain,
    exteriorColor,
    fuelType,
    gearBox,
    interiorColor,
    mileage,
    modelFamily,
    modelYear,
    partnerDealers,
    power,
    price,
    trimLine,
    usedInventoryType,
    vehicleType,
    wheelSize,
    nonAudi,
    otherMakes,
    nonAudiModels,
    preused,
  } = confirmedFilters;

  // Only Tier 3
  const shouldFilterNonAudi = useMemo(() => {
    if (vehicleType === 'new') return false;
    if (nonAudi === 'foreign-brand.yes') return true;
    if (
      usedInventoryType === 'has-warranties.combined' ||
      usedInventoryType === 'has-warranties.nonAudi'
    )
      return true;
    return false;
  }, [nonAudi, usedInventoryType, vehicleType]);

  // Following vars for location filter part
  // ------------------------------------------------------------------------------------
  const userLocation = useMemo(() => {
    // ASK PAVEL ABOUT THE NEXT CONDITION
    if (isTier3page) return undefined;
    if (!location?.selectedUserLocation) return undefined;
    const { selectedUserLocation, radius } = location;
    const { latitude, longitude } = selectedUserLocation.userCoordinates;
    return {
      latitude,
      longitude,
      // if there is no radius then it means search BY_DEALERS so we need to pass 10,000 like the old VLP version
      maxDistance: radius ?? 10000,
    };
  }, [isTier3page, location]);

  const dealersList = useMemo(() => {
    if (isTier3page && partnerDealers?.length > 0) {
      return {
        id: 'dealer',
        items: partnerDealers,
      };
    }
    if (!location) return undefined;
    const { dealershipList, mode } = location;
    if (mode === 'BY_DEALERS') {
      return {
        id: 'dealer',
        items: dealershipList.map(({ kvpsSyncId }: { kvpsSyncId: string }) => kvpsSyncId),
      };
    }
    return {};
  }, [isTier3page, location, partnerDealers]);
  // ------------------------------------------------------------------------------------

  // Vehicle Type Filters (radio selectors)
  // ------------------------------------------------------------------------------------

  const vehiclesType = vehicleType === 'used' ? 'USED' : 'NEW';

  const vehicleAvailabilityFilter =
    availableInMonths === 0 && vehicleType === 'new'
      ? {
          id: 'available-from-code',
          items: ['now'],
        }
      : undefined;

  const usedInventoryTypeFilter = useMemo(() => {
    if (vehicleType === 'new') return undefined;
    if (!isTier3page) {
      if (usedInventoryType === 'has-warranties.yes') {
        return {
          id: 'has-warranties',
          items: ['1'],
        };
      }
      if (usedInventoryType === 'has-warranties.no') {
        return {
          id: 'has-warranties',
          items: ['0'],
        };
      }
    }
    if (isTier3page) {
      // All Audi Preowned
      if (usedInventoryType === 'has-warranties.all') {
        return {
          id: 't_preowned',
          items: ['AudiPreowned'],
        };
      }
      // Audi CPO
      if (usedInventoryType === 'has-warranties.yes') {
        return {
          id: 't_preowned',
          items: ['AudiCertified'],
        };
      }
      // Non Audi
      if (usedInventoryType === 'has-warranties.nonAudi') {
        return {
          id: 't_preowned',
          items: ['ForeignPreown'],
        };
      }
      // All Audi and Non Audi Combined
      if (usedInventoryType === 'has-warranties.combined') {
        return {
          id: 't_preowned',
          items: ['all'],
        };
      }
    }

    return undefined;
  }, [isTier3page, usedInventoryType, vehicleType]);

  const inTransitVehicleFilter = useMemo(() => {
    return {
      id: 'order-status',
      items: ['factory'],
    };
  }, []);
  // ------------------------------------------------------------------------------------

  // To split when we need JDP data, and when DDC
  // ------------------------------------------------------------------------------------
  const importDb = useMemo(() => {
    const isUsMarket = marketType === 'USA';

    if (isTier3page && dataSource === 'mock') return 'AGC_USA_NARWHAL'; // CA and US T3 and Mock Vehicles crated by NARWHAL team
    if (!isUsMarket) return 'AGC_CAN_JDP';
    return 'AGC_USA_JDP';
    // if (isTier3page && dataSource === 'jdp') {
    //   return isUsMarket ? 'AGC_USA_JDP' : 'AGC_CAN_JDP'; // CA OR US T3 and source JDP
    // }
    // if (isUsMarket) return 'AGC_USA_JDP'; // US T1 or US T3 with default dataSource
    // if (isUsedVehicles) return 'X'; // CA Used T1 or CA Used T3 with default dataSource
    // return 'CANN'; // CA NEW T1 or CA NEW T3 with default dataSource
  }, [dataSource, isTier3page, marketType]);

  const importFilter = {
    id: 'stat-import',
    items: [importDb],
  };

  // ------------------------------------------------------------------------------------

  // Regular Filters (checkboxes)
  // ------------------------------------------------------------------------------------
  const modelRangeFilters =
    modelFamily.length && !shouldFilterNonAudi
      ? {
          id: 'model-range',
          items: modelFamily,
        }
      : undefined;

  const bodyTypeFilters =
    bodyType.length && !shouldFilterNonAudi
      ? {
          id: 'body-type',
          items: bodyType,
        }
      : undefined;

  const trimlineFilters =
    trimLine.length && !shouldFilterNonAudi
      ? {
          id: 'trimline',
          items: trimLine,
        }
      : undefined;

  const gearboxFilters =
    gearBox.length && !shouldFilterNonAudi
      ? {
          id: 'gear-type',
          items: gearBox,
        }
      : undefined;

  const drivetrainFilters =
    drivetrain.length && !shouldFilterNonAudi
      ? {
          id: 'drive',
          items: drivetrain,
        }
      : undefined;

  const wheelSizeFilters =
    wheelSize.length && !shouldFilterNonAudi
      ? {
          id: 'wheel-size',
          items: wheelSize,
        }
      : undefined;

  const fuelTypeFilters =
    fuelType.length && !shouldFilterNonAudi
      ? {
          id: 'vtp-drivetrain',
          items: fuelType,
        }
      : undefined;

  const extColorFilters =
    exteriorColor.length && !shouldFilterNonAudi
      ? {
          id: 'exterior-color',
          items: exteriorColor,
        }
      : undefined;

  const intColorFilters =
    interiorColor.length && !shouldFilterNonAudi
      ? {
          id: 'interior-color-seats',
          items: interiorColor,
        }
      : undefined;

  const yearFilters = modelYear.length
    ? {
        id: 'sales-model-year',
        items: modelYear,
      }
    : undefined;

  // ------------------------------------------------------------------------------------

  // Range Filters (select-dropdown)
  // ------------------------------------------------------------------------------------

  const priceFrom =
    price.length > 1
      ? {
          id: 'prices.retail-fr',
          items: [price[0].toString()],
        }
      : undefined;

  const priceTo =
    price.length > 1
      ? {
          id: 'prices.retail-to',
          items: [price[1].toString()],
        }
      : undefined;

  const powerFrom =
    power.length > 1
      ? {
          id: 'powerKw-fr',
          items: [power[0].toString()],
        }
      : undefined;

  const powerTo =
    power.length > 1
      ? {
          id: 'powerKw-to',
          items: [power[1].toString()],
        }
      : undefined;

  const mileageFrom =
    mileage.length > 1 && vehicleType !== 'new'
      ? {
          id: 'usedCarMileage-fr',
          // TEMP SOLUTION
          items: ['2000'],
        }
      : undefined;

  const mileageTo =
    mileage.length > 1
      ? {
          id: 'usedCarMileage-to',
          items: [mileage[1].toString()],
        }
      : undefined;

  const soldFilter = {
    id: 'sold-order',
    items: ['no'],
  };

  // ------------------------------------------------------------------------------------

  // Non Audi brands
  // ------------------------------------------------------------------------------------
  const partnerDealersFilter = useMemo(() => {
    if (!isTier3page) return undefined;
    // All Audi Preowned or Audi CPO
    if (partnerDealers?.length) {
      return {
        id: 'dealer',
        items: partnerDealers,
      };
    }
    return undefined;
  }, [isTier3page, partnerDealers]);

  const nonAudiFilter = useMemo(() => {
    if (vehicleType === 'new') return undefined;
    if (!isTier3page && vehicleType !== 'new') {
      return {
        id: 'foreign-brand',
        items: ['no'],
      };
    }
    return undefined;
  }, [isTier3page, vehicleType]);

  const otherMakesFilter = useMemo(() => {
    if (vehicleType === 'new') return undefined;
    // All Audi Preowned or Audi CPO
    if (usedInventoryType === 'has-warranties.all' || usedInventoryType === 'has-warranties.yes') {
      return undefined;
    }
    // Combined or non Audi
    if (shouldFilterNonAudi) {
      return {
        id: 't_manuf',
        items: otherMakes,
      };
    }
    return undefined;
  }, [otherMakes, shouldFilterNonAudi, usedInventoryType, vehicleType]);

  const nonAudiModelsSelected = useMemo(() => {
    if (vehicleType === 'new' || !nonAudiModels?.length) return undefined;
    // All Audi Preowned or Audi CPO
    if (usedInventoryType === 'has-warranties.all' || usedInventoryType === 'has-warranties.yes') {
      return undefined;
    }
    // Combined or non Audi
    if (shouldFilterNonAudi) {
      return {
        id: 't_model',
        items: nonAudiModels,
      };
    }
    return undefined;
  }, [nonAudiModels, shouldFilterNonAudi, usedInventoryType, vehicleType]);

  const vehiclePreused = preused?.length
    ? {
        id: 'vehicle-preused',
        items: preused,
      }
    : undefined;

  // ------------------------------------------------------------------------------------

  const combinedFilters = [
    modelRangeFilters,
    bodyTypeFilters,
    trimlineFilters,
    gearboxFilters,
    drivetrainFilters,
    wheelSizeFilters,
    fuelTypeFilters,
    extColorFilters,
    intColorFilters,
    importFilter,
    yearFilters,
    priceFrom,
    priceTo,
    powerFrom,
    powerTo,
    mileageFrom,
    mileageTo,
    usedInventoryTypeFilter,
    vehicleAvailabilityFilter,
    // For Pavel - should we add this?
    soldFilter,
    // ONLY TIER 3
    nonAudiFilter,
    otherMakesFilter,
    nonAudiModelsSelected,
    vehiclePreused,
    // partnerDealersFilter,
  ].filter((singleFilter) => singleFilter);

  const shouldListVehicles = useMemo(() => {
    if (isTier3page && !!listOfDealers) return true;
    if (
      !!location?.isPartnerHeader ||
      !!location?.isPreSetPartner ||
      !!location?.selectedUserLocation
    )
      return true;
    return false;
  }, [
    isTier3page,
    listOfDealers,
    location?.isPartnerHeader,
    location?.isPreSetPartner,
    location?.selectedUserLocation,
  ]);

  return { userLocation, dealersList, vehiclesType, importDb, shouldListVehicles, combinedFilters };
};

export default useApiFiltersProcessor;
